import React from 'react';

/**
 * Create-mode step flow pieces shared by EmployeeForm, VendorForm, and
 * CorpForm (#124, #126).
 *
 * StepFlow renders the compact one-row step header: "Step n of y" plus the
 * named steps. Done steps are checkmarked and clickable to go back; the
 * current step is highlighted; upcoming steps are muted.
 *
 *   <StepFlow steps={['Details', 'Contacts']} current={step} onStepClick={goToStep} />
 */
function StepFlow({ steps, current, onStepClick }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '10px',
      marginBottom: '20px',
      paddingBottom: '12px',
      borderBottom: '1px solid var(--hairline)',
    }}>
      <span style={{
        fontFamily: 'var(--font-sans)',
        fontSize: '12px',
        color: 'var(--muted)',
        whiteSpace: 'nowrap',
        marginRight: '4px',
      }}>
        Step {current} of {steps.length}
      </span>
      {steps.map((label, i) => {
        const n = i + 1;
        const done = n < current;
        const active = n === current;
        return (
          <React.Fragment key={label}>
            {i > 0 && (
              <span aria-hidden="true" style={{ width: '14px', height: '1px', background: 'var(--hairline)' }} />
            )}
            <button
              type="button"
              onClick={done && onStepClick ? () => onStepClick(n) : undefined}
              disabled={!done}
              aria-current={active ? 'step' : undefined}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: done ? 'pointer' : 'default',
                fontFamily: 'var(--font-sans)',
                fontSize: '13px',
                fontWeight: active ? 600 : 500,
                letterSpacing: 0,
                color: active ? 'var(--ink)' : done ? 'var(--muted)' : 'var(--muted-soft)',
              }}
            >
              <span aria-hidden="true" style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                fontSize: '11px',
                fontWeight: 600,
                lineHeight: 1,
                background: active ? 'var(--primary)' : 'transparent',
                color: active ? 'var(--on-primary)' : done ? 'var(--primary)' : 'var(--muted-soft)',
                border: active ? '1px solid var(--primary)' : '1px solid var(--hairline)',
              }}>
                {done ? '✓' : n}
              </span>
              {label}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/**
 * One-line rule shown before any interaction on a Contacts step or tab, so a
 * contact requirement or consequence is never a surprise at submit time.
 */
export function FlowNote({ children }) {
  return (
    <p style={{
      margin: '0 0 16px',
      fontFamily: 'var(--font-sans)',
      fontSize: '13px',
      color: 'var(--body-text)',
    }}>
      {children}
    </p>
  );
}

/**
 * Small coral dot appended to a tab label to flag a missing consequential
 * entry (a required email contact, a Billing email, no contacts at all).
 */
export function TabAttentionDot({ label = 'required' }) {
  return (
    <span
      role="img"
      aria-label={label}
      style={{
        display: 'inline-block',
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        background: 'var(--primary)',
        marginLeft: '6px',
        verticalAlign: 'middle',
      }}
    />
  );
}

export default StepFlow;
